import classnames from 'classnames'
import { FormikProvider, useFormik } from 'formik'
import React, { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useUser } from '../../../state/entities'
import useUpdateUserLearningAssignment from '../../../state/mutations/useUpdateUserLearningAssignment'
import useUserLearningItem from '../../../state/queries/useUserLearningItem'
import FormButtons from '../../basics/FormButtons/FormButtons'
import { TextInputFormik } from '../../basics/TextInput/TextInput'
import AssignedBySlug from '../../slugs/AssignedBySlug'
import UserSlug from '../../slugs/UserSlug/UserSlug'
import LearningItemAvatar from '../../LearningItemAvatar/LearningItemAvatar'

import styles from './EditUserLearningAssignmentForm.module.scss'
import { useSession } from '../../../state/session'
import {
  managerCanEditLearningAssignmentOfGroupMember,
  managerCanEditLearningAssignmentOfGroupMemberWhenAssignmentWasAddedDirectly,
  userCanEditUserLearningAssignment,
} from '../../../state/calculations/permissions/userCan'

// import ClearIcon from '@material-symbols/svg-300/rounded/cancel.svg'

interface EditUserLearningAssignmentFormProps {
  userUuid: string
  learningItemUuid: string
  onClose: () => void
}

export default React.memo(function EditUserLearningAssignmentForm({
  userUuid,
  learningItemUuid,
  onClose,
}: EditUserLearningAssignmentFormProps) {
  const { t } = useTranslation()

  const { user: currentUser } = useSession()

  // User & learning item
  const { user } = useUser(userUuid)

  const { userLearningItem } = useUserLearningItem(userUuid, learningItemUuid)

  const updateUserLearningAssignment = useUpdateUserLearningAssignment()

  const initialValues = useMemo(
    () =>
      Object.fromEntries(
        userLearningItem.userLearningAssignments?.map((assignment) => [
          assignment.uuid,
          {
            dueDate: assignment?.dueDate,
          },
        ]) || []
      ),
    [userLearningItem]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values) => {
      // console.log(values)
      try {
        for (const [assignmentUuid, data] of Object.entries(values)) {
          await updateUserLearningAssignment.mutateAsync({
            uuid: assignmentUuid,
            dueDate: data.dueDate || undefined,
          })
        }
        onClose()
      } catch (err) {
        console.log(err)
      }
    },
  })

  if (!userLearningItem) {
    throw new Error('Learning item not found')
  }

  return (
    <article className={classnames(styles.EditUserLearningAssignmentForm)}>
      <header>
        <hgroup>
          <h1>
            <Trans i18nKey="manage learning.edit learning assignment for user">
              <UserSlug user={user} />
            </Trans>
          </h1>
        </hgroup>
      </header>

      <div className={styles.learningItem}>
        <LearningItemAvatar learningItem={userLearningItem} />
        <div>{userLearningItem.title}</div>
      </div>

      <FormikProvider value={formik}>
        <form noValidate onSubmit={formik.handleSubmit}>
          <table>
            <thead>
              <tr>
                <th>{t('learning item.assigned by')}</th>
                <th>{t('learning item.assigned at')}</th>
                <th>{t('learning item.due date')}</th>
              </tr>
            </thead>
            <tbody>
              {userLearningItem.userLearningAssignments?.map((assignment) => {
                const canEdit =
                  userCanEditUserLearningAssignment(currentUser) ||
                  (!!assignment.assignedByGroupUuid &&
                    managerCanEditLearningAssignmentOfGroupMember(currentUser?.managedGroups || [], user.groups || [], [
                      { groupUuid: assignment.assignedByGroupUuid },
                    ])) ||
                  (!!assignment.assignedByUserUuid &&
                    managerCanEditLearningAssignmentOfGroupMemberWhenAssignmentWasAddedDirectly(
                      currentUser?.managedGroups || [],
                      user.groups || [],
                      assignment.assignedByUserUuid === currentUser?.uuid
                    ))

                return <AssignmentRow key={assignment.uuid} assignment={assignment} canEdit={canEdit} />
              })}
            </tbody>
          </table>
          <FormButtons onClose={onClose} {...formik} />
        </form>
      </FormikProvider>
    </article>
  )
})

function AssignmentRow({
  assignment,
  canEdit,
}: {
  canEdit: boolean
  assignment: {
    uuid: string
    createdAt?: Date | null | undefined
    dueDate?: Date | null | undefined
    assignedByGroupUuid?: string | null | undefined
    assignedByUserUuid?: string | null | undefined
  }
}) {
  const { t } = useTranslation()
  // const formik = useFormikContext();
  const inputName = `${assignment.uuid}.dueDate`

  return (
    <tr key={assignment.uuid}>
      <td>
        <AssignedBySlug assignment={assignment} />
      </td>
      <td>{t('format.DATE', { date: assignment.createdAt })}</td>
      <td>
        <div className={styles.dataColumn}>
          <div>
            <TextInputFormik
              omitErrorDisplay
              label={t('learning item.due date')}
              name={inputName}
              type="date"
              disabled={!canEdit}
            />
          </div>
          <div style={{ display: 'none' }}>
            {/*<button*/}
            {/*  type="button"*/}
            {/*  disabled={ ! canEdit }*/}
            {/*  onClick={ () => {*/}
            {/*    formik.setFieldValue(inputName, undefined);*/}
            {/*  } }*/}
            {/*>*/}
            {/*  <ClearIcon/>*/}
            {/*</button>*/}
          </div>
        </div>
      </td>
    </tr>
  )
}
